import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { MapPin, MessageCircle } from "lucide-react";
import { storeApi } from "../../api/cart";
import { PageWidth } from "../../components/catalogue/StoreShell";
import { Card, Icon } from "../../components/ui";
import { usePageTitle } from "../catalogue/usePageTitle";
import { InquiryForm } from "./InquiryForm";

// PRD screen 19. The words describe only what the shop does today. Draft copy for the product
// owner to correct; delivery charges (KALI-5, KALI-11) and the channels (KALI-1) are open.

function Page({ title, intro, children }: { title: string; intro: string; children: ReactNode }) {
  usePageTitle(title);
  return (
    <PageWidth className="max-w-[760px] pt-8">
      <h1 className="mb-2 type-h1 [font-size:var(--text-2xl)]">{title}</h1>
      <p className="mb-8 type-body text-text-muted">{intro}</p>
      <div className="flex flex-col gap-8">{children}</div>
    </PageWidth>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-3 type-h3 [font-size:var(--text-lg)]">{title}</h2>
      <div className="flex flex-col gap-3 type-body text-text-body [&_p]:m-0">{children}</div>
    </section>
  );
}

function useStore() {
  return useQuery({ queryKey: ["store"], queryFn: storeApi.get, staleTime: Infinity });
}

function PickupPoint() {
  const pickup = useStore().data?.pickup_point;
  if (!pickup) return null;
  return (
    <p className="flex items-start gap-2">
      <Icon icon={MapPin} size={18} className="mt-1 flex-none text-ink-500" />
      <span>
        {pickup.address}
        <span className="block type-small text-text-muted">{pickup.hours}</span>
      </span>
    </p>
  );
}

/**
 * How to reach us (PRD 5.11a). The channels come from config/store.yml; until the business
 * picks the ones it can answer (KALI-1), the message form is the way in, and it works.
 */
export function ContactRoute() {
  const channels = useStore().data?.support_channels ?? [];
  return (
    <Page title="Contact us" intro="Ask whether we have a part, how many, or how soon it can reach you.">
      {channels.length > 0 && (
        <Section title="Talk to us">
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {channels.map((channel) => (
              <li key={channel.label} className="flex items-center gap-3">
                <Icon icon={MessageCircle} size={18} className="text-ink-500" />
                <span className="w-28 type-small text-text-muted">{channel.label}</span>
                {channel.href ? <a href={channel.href}>{channel.value}</a> : <span className="type-sku text-text-strong">{channel.value}</span>}
              </li>
            ))}
          </ul>
        </Section>
      )}
      <Section title="Send us a message">
        <p>{channels.length > 0 ? "Or write to us here. We reply by email or phone." : "Write to us here and we'll reply by email or phone, usually within a working day."}</p>
        <Card padding="var(--space-6)">
          <InquiryForm />
        </Card>
      </Section>
      <Section title="Collect from us">
        <PickupPoint />
      </Section>
    </Page>
  );
}

export function DeliveryRoute() {
  const busLines = useStore().data?.bus_lines.map((line) => line.name) ?? [];
  return (
    <Page title="Delivery" intro="Every order is delivered to your address, sent to a bus terminal for you to collect, or collected from us. Near Quezon City, it can come the same day. You choose at checkout.">
      <Section title="Delivered to you">
        <p>
          After you place an order, we email you the delivery charge. Then you pay the full total, parts and delivery, in one bank transfer. Or choose cash on delivery
          and pay the courier when it arrives.
        </p>
        <p>We tell you when it's on its way, by email, with the courier's tracking number.</p>
      </Section>
      <Section title="Urgent local delivery">
        <p>
          For a machine that can't wait: we send it by Lalamove or Grab, to towns within 120 km of Quezon City that riders can reach by road. Checkout tells you if your
          address is one of them.
        </p>
        <p>
          It goes out the same day if we've confirmed your payment before 3 PM (or, for cash on delivery, you ordered before 3 PM), otherwise the next working day. We send urgent orders Monday to Saturday, not on Sundays
          or public holidays. There's no delivery charge from us: you pay the rider directly when it arrives.
        </p>
      </Section>
      <Section title="Collect it from a bus terminal">
        <p>
          Choose bus terminal pickup at checkout, then the bus line{busLines.length > 0 && ` (${new Intl.ListFormat("en", { type: "disjunction" }).format(busLines)})`} and the terminal
          where you'll collect it. We send it by bus cargo. The bus fee is part of the delivery charge, which we email you before you pay. Bus terminal pickup is paid by bank transfer: the bus lines don't collect cash.
        </p>
        <p>When it's on the bus, we email you the waybill number. Give it at the terminal to collect your order.</p>
      </Section>
      <Section title="Collect it yourself">
        <p>Choose pickup at checkout, and pay by bank transfer or at our counter when you collect. We email you when your order is ready. Whoever collects it should give the order number.</p>
        <PickupPoint />
      </Section>
      <Section title="Questions">
        <p>
          Need it by a certain day, or not sure we deliver to you? <Link to="/contact">Ask us first</Link>.
        </p>
      </Section>
    </Page>
  );
}

export function ReturnsRoute() {
  return (
    <Page title="Cancellations and returns" intro="What you can change after ordering, and what to do if a part is wrong.">
      <Section title="Cancelling an order">
        <p>Until we start packing it, you can cancel an order yourself from its page in your account. If you've already paid, we contact you to return the money.</p>
        <p>
          Once we're packing it, <Link to="/contact">contact us</Link> and we'll do our best. Once it's on its way, it can't be cancelled.
        </p>
      </Section>
      <Section title="A wrong or damaged part">
        <p>
          <Link to="/contact">Contact us</Link> with your order number and what's wrong. A photo helps. We'll arrange a replacement or a return with you by phone.
        </p>
      </Section>
      <Section title="Before you order">
        <p>
          Check the part against your machine's diagram and the number on the old part. If you're not sure it's the right one, <Link to="/contact">ask us</Link>.
        </p>
      </Section>
    </Page>
  );
}
